import GalleryComponent, { GallerySize } from "components/Media/Gallery";
import { importAll, transformImages } from "utils";

import ReplicaBigTitle from "components/Replicas/ReplicaBigTitle";
import ReplicaParagraph from "components/Replicas/ReplicaParagraph";
import ReplicaQuote from "components/Replicas/ReplicaQuote";
import ReplicaSpacer from "components/Replicas/ReplicaSpacer";
import ReplicaSubTitleAnchor from "components/Replicas/ReplicaSubTitleAnchor";

let galleryAssetsFontsRaw = [];
let galleryAssetsFontsRaw2 = [];
let galleryAssetsFontsRaw3 = [];
let galleryAssetsAdsRaw = [];
let galleryAssetsColorsRaw = [];
let galleryAssetsFilmRaw = [];
let galleryAssetsArticleOllivanderRaw = [];
let galleryAssetsFoilingRaw = [];

try {
  galleryAssetsFontsRaw = importAll(
    (require as any).context("./assets-fonts", false, /\.(png|jpe?g|svg)$/)
  );
  galleryAssetsFontsRaw2 = importAll(
    (require as any).context("./assets-fonts-2", false, /\.(png|jpe?g|svg)$/)
  );
  galleryAssetsFontsRaw3 = importAll(
    (require as any).context("./assets-fonts-3", false, /\.(png|jpe?g|svg)$/)
  );
  galleryAssetsAdsRaw = importAll(
    (require as any).context("./assets-ads", false, /\.(png|jpe?g|svg)$/)
  );
  galleryAssetsColorsRaw = importAll(
    (require as any).context("./assets-color", false, /\.(png|jpe?g|svg)$/)
  );
  galleryAssetsFilmRaw = importAll(
    (require as any).context("./assets-film", false, /\.(png|jpe?g|svg)$/)
  );
  galleryAssetsArticleOllivanderRaw = importAll(
    (require as any).context(
      "./assets-article-oliv",
      false,
      /\.(png|jpe?g|svg)$/
    )
  );
  galleryAssetsFoilingRaw = importAll(
    (require as any).context("./assets-foiling", false, /\.(png|jpe?g|svg)$/)
  );
} catch (e) {
  console.warn(e);
}

const QuibblerMakingOf = () => {
  const galleryAssetsFonts = transformImages(galleryAssetsFontsRaw);
  const galleryAssetsFonts2 = transformImages(galleryAssetsFontsRaw2);
  const galleryAssetsFonts3 = transformImages(galleryAssetsFontsRaw3);
  const galleryAssetsAds = transformImages(galleryAssetsAdsRaw);
  const galleryAssetsColors = transformImages(galleryAssetsColorsRaw);
  const galleryAssetsFilm = transformImages(galleryAssetsFilmRaw);
  const galleryAssetsArticleOllivander = transformImages(
    galleryAssetsArticleOllivanderRaw
  );
  const galleryAssetsFoiling = transformImages(galleryAssetsFoilingRaw);

  return (
    <>
      <ReplicaBigTitle title="Making Of" />

      <ReplicaQuote source="Harry Potter and the Order of the Phoenix">
        “Anything good in there?” asked Ron as Harry closed the magazine. “Of
        course not,” said Hermione scathingly, before Harry could answer, “The
        Quibbler’s rubbish, everyone knows that.”
      </ReplicaQuote>
      <ReplicaSpacer />

      <ReplicaParagraph>
        From the very beginning, I was determined to create complete magazines
        for each Quibbler cover that had been designed for the films. As always,
        the first step was extensive research - gathering as much information
        about the original props as possible, and combing through all the Harry
        Potter books for any mentions of The Quibbler, its articles, or
        descriptions. I then compiled all the pre-existing pages as a point of
        reference and began building an asset library, including layout
        elements, advertisements, and more, which I could later draw from when
        designing the individual issues.
      </ReplicaParagraph>

      <ReplicaSubTitleAnchor title="Faithful to the books and the films" />

      <ReplicaParagraph>
        When gathering the list of articles that would need to be featured in
        any given issue, I had to take multiple sources of information into
        account, as certain articles were required to appear in the individual
        issues. I first looked at the front cover, which often features article
        headings with page numbers — those needed to be included, of course.
        Then, after assigning each reference page to a specific issue, the
        articles featured on those pages naturally had to be included — which
        meant I had to write the full texts myself, based on the original
        headings. I also found one reference image showing a list of contents,
        so every article mentioned there had to appear on the correct page
        inside the magazine as well. After all the visual information was
        covered, I turned to the books for any mention of the issue I was
        working on. Sometimes, the books included article titles, descriptions,
        or even excerpts — in those cases, I used the original wording exactly
        as it appears, and then expanded it into a complete article.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent images={galleryAssetsFilm} size={GallerySize.XL} />
      <ReplicaSpacer />

      <ReplicaSubTitleAnchor
        title="Readable and Relevant Articles"
        subtitle="Playing the Editor in Chief"
      />

      <ReplicaParagraph>
        After all the must-have articles were accounted for, I had to create
        original content to fill out each magazine. This turned out to be quite
        a challenge, as every new article needed to match The Quibbler’s unique
        tone. The Quibbler is known for its wild conspiracy theories and
        outrageous claims, all presented with complete conviction. While coming
        up with bizarre topics was certainly fun, the real challenge was in the
        'voice'. The Quibbler doesn’t just report on absurd things — that would
        be straightforward journalism. Instead, it writes absurdly. It’s the
        difference between reading an article about a madman spouting nonsense —
        something you’d find in The Daily Prophet — and reading an article
        written by the madman himself, fully convinced that every word he writes
        is true - now THAT is The Quibbler.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <ReplicaQuote source="Harry Potter and the Half-Blood Prince">
        “Talking of Diagon Alley,” said Mr. Weasley, “looks like Ollivander’s
        gone too.” “The wandmaker?” said Ginny, looking startled. “That’s the
        one. Shop’s empty. No sign of a struggle. No one knows whether he left
        voluntarily or was kidnapped.”
      </ReplicaQuote>
      <ReplicaSpacer />

      <ReplicaParagraph>
        I also took great care to accurately reference plot-related events and
        include hidden easter eggs. For example, in The Half-Blood Prince, it’s
        revealed that Ollivander has disappeared (kidnapped by You-Know-Who), so
        I referenced his absence in one of the issues set sometime after that
        point. Naturally, the article wrapped his disappearance in an absurd
        conspiracy theory—since it wasn’t publicly known that he’d been
        kidnapped. According to the piece, he went into hiding to escape public
        outrage after wand-rights activists “discovered” that wands need sleep
        just like us, and that we’ve been overworking them. Sounds legit.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent
        images={galleryAssetsArticleOllivander}
        imagesPerRow={4}
        imageOptions={[{ index: 1, aspectRatio: 2 }]}
        imageMaxHeight="250px"
      />
      <ReplicaSpacer />

      <ReplicaParagraph>
        Setting the issues correctly within the timeline of the plot also meant
        accurately referencing key details — such as who the current Minister
        for Magic was — but that wasn’t the only challenge. It was equally
        important to gradually shift the magazine’s tone in later issues. The
        Quibbler needed to evolve from a light-hearted, quirky publication into
        a more serious one, reflecting the rising tension as the Second
        Wizarding War unfolded. Eventually, the magazine fell under Ministry
        control and began running content like the “Undesirable No. 1” campaign
        against Harry Potter. I assume that by this point, the Ministry
        (effectively controlled by the Death Eaters) would have kept Xenophilius
        on a tight leash, with articles increasingly promoting propaganda
        against Muggle-borns.
      </ReplicaParagraph>

      <ReplicaSubTitleAnchor title="Fonts" subtitle="Asset Library" />

      <ReplicaParagraph>
        Just like when I worked on replicas of The Daily Prophet, one of the
        most challenging parts was figuring out which fonts were used — and
        there are many. This was perhaps the most frustrating step, because
        identifying fonts based on just a few letters is harder than it sounds,
        and spending endless nights scrolling through fonts one after another is
        even less fun than it sounds. Acquiring fonts required careful
        consideration of both budget and time. For example, if I needed a font
        for just one word, I sometimes ended up recreating that word as an image
        when the font was difficult to obtain or too expensive for a single-word
        use. There were also times when I had to create a font myself from
        scratch, especially if it was used frequently, or tweak a pre-existing
        font that didn’t match the original perfectly but was the best available
        option. In the end, I ended up with more than 150 fonts.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent images={galleryAssetsFonts} size={GallerySize.M} />
      <ReplicaSpacer />

      <ReplicaParagraph>
        One time, I deliberately strayed from the original by adjusting a font
        to make it more readable. This font was used for the articles, and
        although I found the original, it was far too difficult to read in small
        text. I believe this was a deliberate choice by the original
        prop-makers, intended to mask the fact that the texts in the props were
        just filler with no real meaning. However, since my replicas were meant
        to be read, I decided to tweak the font slightly to improve readability
        while still keeping it looking like the original at first glance.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent images={galleryAssetsFonts2} size={GallerySize.M} />
      <ReplicaSpacer />

      <ReplicaParagraph>
        It’s also important to note that while researching the original props, I
        found that the handful of pages designed for the films featured
        different fonts on some pages. Based on the content, I believe these
        pages were created at different times, which led me to conclude that the
        font should change issue-to-issue rather than page-to-page. For example,
        the pages covering “How Far Will Fudge Go To Gain Gringotts” and the
        article about Sirius Black—both part of the same issue—use the same
        font, which differs from the fonts used in pages designed for the later
        movies. I organized the reference pages by the issues they belong to and
        used that to determine the main font for each issue. Since some issues,
        like Issue No. 1, didn’t have any pages designed at all, I was free to
        choose any font I wanted for those.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent images={galleryAssetsFonts3} size={GallerySize.M} />

      <ReplicaSubTitleAnchor title="Classifieds" subtitle="Asset Library" />

      <ReplicaParagraph>
        Although The Quibbler can't rival The Daily Prophet in the number of
        advertisements, there's still a large amount of classified ads present.
        All of these needed to be created from scratch, of course. I suspect
        that the originals were picked from old magazines or other publications
        and re-used in the props, because I found (unfortunately just a few)
        images where I could read the text printed on the actual prop — and it's
        clearly not a custom-written ad. For example, there's an ad titled "WAND
        TUNING", but the text actually talks about piano tuning, which makes me
        believe they sourced these ads from somewhere and then simply changed
        the title to better fit the wizarding world. So, in such cases, I took
        the liberty of updating the text so that it would read as an actual ad
        relevant to its title.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent images={galleryAssetsAds} size={GallerySize.M} />
      <ReplicaSpacer />

      <ReplicaSubTitleAnchor title="In Color" />

      <ReplicaParagraph>
        Another thing I noticed while researching the original prop was
        inconsistent use of color — the very same page sometimes appeared in
        multiple props, but in black-and-white in some cases and in full color
        in others. I concluded that the color versions were meant to be the
        “hero” pages (those actually meant to be seen on screen), while the
        black-and-white versions were used as filler — never intended for
        close-ups, but simply there to give the illusion of a full magazine when
        flipped through. The challenge was that a few pages existed only in
        black-and-white, with no known color versions. In those cases, I took
        the liberty of adding color myself, making choices that felt consistent
        with the look and tone of the originals.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent images={galleryAssetsColors} size={GallerySize.M} />
      <ReplicaSpacer />

      <ReplicaSubTitleAnchor title="Printing & Silver Foiling" />

      <ReplicaParagraph>
        And there you have it! After months of work, I completed nearly 200
        unique pages containing over 400 original articles. I also had to
        determine the final dimensions of the original props — which wasn’t
        entirely straightforward, as I found a photo showing two versions of The
        Quibbler with noticeably different sizes. I assume the size was adjusted
        specifically for the “Spectrespecs” issue to ensure the cut-out glasses
        would be the correct scale. In the end, I decided to keep all of my
        replicas uniform in size for consistency.
      </ReplicaParagraph>

      <ReplicaParagraph>
        After finalizing all the designs and ensuring they were in the correct
        dimensions, I began experimenting with different types of paper and
        weights. I eventually settled on a heavier stock for the cover —
        especially important for supporting the cut-out Spectrespecs — and a
        slightly lighter paper for the inner pages. Getting the final “feel” of
        the prop replica right was essential, and I believe I managed to achieve
        an authentic result.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <GalleryComponent
        images={galleryAssetsFoiling}
        imagesPerRow={4}
        imageOptions={[{ index: 1, aspectRatio: 2 }]}
      />
      <ReplicaSpacer />

      <ReplicaParagraph>
        Since only a few issues of The Quibbler were actually seen in the films,
        it might not be immediately obvious, but some elements on the covers
        were meant to be silver-foiled. While I have a pretty good idea of which
        parts were supposed to be foiled, I unfortunately had to skip a few due
        to the designs being far too intricate and detailed for me to produce by
        myself. I did look into having these stencils professionally
        manufactured, but the process turned out to be prohibitively expensive —
        we’re talking hundreds of dollars for a single page. So, reluctantly, I
        had to leave out some of the finer details and ended up doing all the
        foiling myself. Each issue includes a foiled main logo, and the
        'Spectrespecs' issue features even more extensive foiling.
      </ReplicaParagraph>
    </>
  );
};

export default QuibblerMakingOf;
